import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { languages, getLanguageByCode } from '../i18n/languages'

export default function LanguageSelector({ className = '', size = 'medium', showFlag = true, showName = true }) {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  
  const currentLanguage = getLanguageByCode(i18n.language)
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode)
    setIsOpen(false)
  }
  
  const sizeClasses = {
    small: 'text-xs py-1.5 px-3',
    medium: 'text-sm py-2 px-4',
    large: 'text-base py-3 px-5'
  }
  
  const iconSizes = {
    small: 'h-3.5 w-3.5',
    medium: 'h-4 w-4',
    large: 'h-5 w-5'
  }
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm ${sizeClasses[size]}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className={`${iconSizes[size]} text-gray-500`} />
        {showFlag && <span className="text-base">{currentLanguage.flag}</span>}
        {showName && <span className="font-medium text-gray-700">{currentLanguage.nativeName}</span>}
        <ChevronDown className={`${iconSizes[size]} text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 rounded-xl bg-white shadow-elevated border border-gray-100 z-50 overflow-hidden animate-fade-in">
          <ul role="listbox" className="py-1">
            {languages.map((language) => (
              <li key={language.code}>
                <button
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                    i18n.language === language.code ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                  }`}
                  role="option"
                  aria-selected={i18n.language === language.code}
                >
                  <span className="text-base">{language.flag}</span>
                  <span className="flex-1 font-medium">{language.nativeName}</span>
                  {i18n.language === language.code && (
                    <svg className="h-4 w-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
